// UIUX17: presentation only. Callers own state, eligibility and all event listeners.
export interface TicketPresentationPart {
  root: HTMLElement;
  element<T extends HTMLElement = HTMLElement>(originalId: string): T;
}

function part(markup: string, prefix: string): TicketPresentationPart {
  const template = document.createElement("template");
  template.innerHTML = markup;
  const root = template.content.firstElementChild as HTMLElement;
  const elements = new Map<string, HTMLElement>();
  for (const element of root.querySelectorAll<HTMLElement>("[id]")) {
    elements.set(element.id, element);
    if (prefix) element.id = `${prefix}${element.id}`;
  }
  if (prefix) {
    for (const element of root.querySelectorAll<HTMLElement>("[data-testid]")) {
      element.dataset["testid"] = `${prefix}${element.dataset["testid"]}`;
    }
    for (const element of root.querySelectorAll<HTMLElement>(
      "[aria-labelledby], [aria-describedby], [for]",
    )) {
      for (const attribute of ["aria-labelledby", "aria-describedby", "for"]) {
        const value = element.getAttribute(attribute);
        if (value)
          element.setAttribute(
            attribute,
            value
              .split(/\s+/)
              .map((id) => `${prefix}${id}`)
              .join(" "),
          );
      }
    }
  }
  return {
    root,
    element<T extends HTMLElement>(originalId: string): T {
      const element = elements.get(originalId);
      if (!element) throw new Error(`Missing ticket presentation element: ${originalId}`);
      return element as T;
    },
  };
}

export function createTicketHeader(prefix = ""): TicketPresentationPart {
  return part(TICKET_HEADER, prefix);
}
export function createTicketMoneyFooter(prefix = ""): TicketPresentationPart {
  return part(TICKET_MONEY_FOOTER, prefix);
}

export function mountTicketPresentation(host: ParentNode): void {
  const header = host.querySelector("[data-ticket-presentation=header]");
  const footer = host.querySelector("[data-ticket-presentation=footer]");
  if (!header || !footer) throw new Error("Missing ticket presentation mounts");
  header.replaceWith(createTicketHeader().root);
  footer.replaceWith(createTicketMoneyFooter().root);
}

export function createTicketLineFrame() {
  const row = document.createElement("div");
  row.className = "line";
  row.dataset["testid"] = "ticket-line";
  const info = document.createElement("div");
  info.className = "line-info";
  const top = document.createElement("div");
  top.className = "line-top";
  const name = document.createElement("div");
  name.className = "line-name";
  const nameText = document.createElement("span");
  nameText.className = "line-name-text";
  return { row, info, top, name, nameText };
}

export function createTicketLinePrice(unavailable = false): HTMLDivElement {
  const price = document.createElement("div");
  price.className = unavailable ? "line-price unavailable" : "line-price";
  return price;
}

export function createTicketLineEditButton(): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn-line-edit";
  button.dataset["testid"] = "line-edit-button";
  button.textContent = "Edit";
  return button;
}

export function createTicketStateButton(): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.dataset["testid"] = "prepared-button";
  return button;
}

export function paintTicketState(
  button: HTMLButtonElement,
  stateName: "paid" | "prepared" | "not-prepared",
  refunded: boolean,
): void {
  button.className = `state-btn ${stateName}`;
  button.innerHTML =
    stateName === "paid"
      ? `<span class="dot-icon">&#10003;</span>${refunded ? "Refunded" : "Paid"}`
      : stateName === "prepared"
        ? '<span class="dot-icon">&#10003;</span>Prepared'
        : '<span class="dot-icon"></span>Not Prepared';
}

const TICKET_HEADER = `<div class="ticket-header">
                <div class="ticket-header-row">
                  <span class="ticket-id">
                    <span id="ticket-sub" data-testid="ticket-sub"></span>
                    <span class="ticket-age" id="order-status" data-testid="order-status"
                      >No open ticket</span
                    >
                  </span>
                  
                  <div class="add-guest-row" id="add-guest-row" data-testid="add-guest-row" hidden>
                    
                    <button
                      class="btn-ticket-repeat"
                      type="button"
                      id="btn-ticket-repeat"
                      data-testid="ticket-repeat-button"
                      hidden
                    >
                      Repeat last
                    </button>
                    <button
                      class="btn-ticket-refund"
                      type="button"
                      id="btn-ticket-refund"
                      data-testid="ticket-refund-button"
                      hidden
                    >
                      Refund
                    </button>
                    <button
                      class="btn-add-guest"
                      type="button"
                      id="add-guest-button"
                      data-testid="add-guest-button"
                    >
                      + Guest
                    </button>
                    
                    <button
                      class="btn-ticket-more"
                      type="button"
                      id="btn-ticket-more"
                      data-testid="ticket-more-button"
                      aria-label="More ticket actions"
                      hidden
                    >
                      More &#9662;
                    </button>
                  </div>
                </div>
                
                <div
                  class="ticket-hold-badge"
                  id="ticket-hold-badge"
                  data-testid="ticket-hold-badge"
                  hidden
                ></div>
                
                <div
                  class="ticket-hold-badge ticket-awaiting-badge"
                  id="ticket-awaiting-badge"
                  data-testid="ticket-awaiting-badge"
                  hidden
                >
                  Awaiting tip
                </div>
              </div>`;

const TICKET_MONEY_FOOTER = `<div class="ticket-money-footer">
                <div class="ticket-totals" id="ticket-totals" hidden>
                  <div
                    class="totals-row totals-compact"
                    id="totals-compact"
                    data-testid="totals-compact"
                  >
                    <span>Subtotal</span
                    ><span id="subtotal-amount" data-testid="subtotal-amount"></span>
                  </div>
                  
                  <div class="discount-lines" id="discount-lines" data-testid="discount-lines" hidden></div>
                  <div class="totals-row">
                    <span>Tax</span><span id="tax-amount" data-testid="tax-amount"></span>
                  </div>
                  <span
                    class="totals-price-changes"
                    id="price-changes-note"
                    data-testid="price-changes-note"
                    hidden
                  ></span>
                  <div class="totals-row-grand" id="totals-toggle" data-testid="totals-toggle">
                    <span class="totals-label-row" id="total-label" data-testid="total-label">Total</span>
                    <span id="total-amount" data-testid="total-amount"></span>
                  </div>
                  <!-- DUALPRICE1: with dual pricing on the guest and the bartender see both totals; Pay with card charges the Card total. -->
                  <div class="dual-pricing-totals" id="dual-pricing-totals" data-testid="dual-pricing-totals" hidden>
                    <div class="totals-row">
                      <span>Cash</span><span id="dual-cash-amount" data-testid="dual-cash-amount"></span>
                    </div>
                    <div class="totals-row">
                      <span>Card</span><span id="dual-card-amount" data-testid="dual-card-amount"></span>
                    </div>
                    <div class="totals-row dual-pricing-note">
                      <span id="dual-card-note" data-testid="dual-card-note"></span>
                    </div>
                  </div>
                  
                  <div class="totals-row-paid" id="paid-row" data-testid="paid-row" hidden>
                    <span class="paid-label"
                      ><span class="paid-check" aria-hidden="true">&#10003;</span>Paid</span
                    >
                    <span
                      ><span id="paid-amount" data-testid="paid-amount"></span>
                      <span class="paid-count" id="paid-count" data-testid="paid-count"></span
                    ></span>
                  </div>
                  
                  <div class="totals-row-paid" id="authorized-row" data-testid="authorized-row" hidden>
                    <span class="paid-label"
                      ><span class="paid-check" aria-hidden="true">&#10003;</span>Authorized</span
                    >
                    <span
                      ><span id="authorized-amount" data-testid="authorized-amount"></span>
                      <span
                        class="paid-count"
                        id="authorized-card"
                        data-testid="authorized-card"
                      ></span
                    ></span>
                  </div>
                  <div class="totals-row" id="awaiting-tip-row" data-testid="awaiting-tip-row" hidden>
                    <span>Tip</span><span>Not entered</span>
                  </div>
                  
                  <div class="totals-row-tips" id="tips-row" data-testid="tips-row" hidden>
                    <span>Tips</span><span id="tips-amount" data-testid="tips-amount"></span>
                  </div>
                  
                  <div class="totals-row-charged" id="charged-row" data-testid="charged-row" hidden></div>

                  <div class="totals-row" id="balance-row" data-testid="balance-row" hidden>
                    <span>Balance</span><span id="balance-amount" data-testid="balance-amount"></span>
                  </div>
                  
                  <div
                    class="totals-row-refunded"
                    id="refunded-row"
                    data-testid="refunded-row"
                    hidden
                  >
                    <span>Refunded</span
                    ><span id="refunded-amount" data-testid="refunded-amount"></span>
                  </div>

                </div>

                
                <div
                  class="zero-due-prompt"
                  id="zero-due-prompt"
                  data-testid="zero-due-prompt"
                  hidden
                >
                  
                  <span
                    class="zero-due-prompt-text"
                    id="zero-due-prompt-text"
                    data-testid="zero-due-prompt-text"
                    >All paid. Close this ticket?</span
                  >
                  <div class="zero-due-prompt-actions">
                    <button
                      class="btn-secondary btn-compact"
                      type="button"
                      id="zero-due-dismiss"
                      data-testid="zero-due-dismiss"
                    >
                      Not yet
                    </button>
                    <button
                      class="btn-pay btn-compact"
                      type="button"
                      id="zero-due-close"
                      data-testid="zero-due-close"
                    >
                      Close ticket
                    </button>
                  </div>
                </div>

                <div class="ticket-actions" id="ticket-actions" hidden>
                  
                  
                  <div
                    class="action-row-primary"
                    id="awaiting-tip-actions"
                    data-testid="awaiting-tip-actions"
                    hidden
                  >
                    <button
                      class="btn-secondary awaiting-tip-reprint"
                      type="button"
                      id="awaiting-tip-reprint"
                      data-testid="awaiting-tip-reprint"
                    >
                      Reprint
                    </button>
                    <button
                      class="btn-pay"
                      type="button"
                      id="awaiting-tip-enter"
                      data-testid="awaiting-tip-enter"
                    >
                      Enter tip
                    </button>
                  </div>
                  <div
                    class="pay-status"
                    id="awaiting-tip-status"
                    data-testid="awaiting-tip-status"
                    role="status"
                    hidden
                  ></div>
                  <div class="action-row-primary">
                    <button
                      class="btn-prepare-all"
                      type="button"
                      id="prepare-all"
                      data-testid="prepare-all"
                    >
                      Prepare All
                    </button>
                    <button class="btn-pay" type="button" id="pay-button" data-testid="pay-button">
                      Pay
                    </button>
                  </div>
                  
                  <button
                    class="btn-secondary btn-full"
                    type="button"
                    id="no-tip-button"
                    data-testid="no-tip-button"
                    hidden
                  >
                    No tip, close ticket
                  </button>
                  <div
                    class="pay-block-reason"
                    id="pay-block-reason"
                    data-testid="pay-block-reason"
                    hidden
                  ></div>
                  
                  <div class="pay-fail" id="pay-fail" data-testid="pay-fail" role="status" hidden>
                    <span class="pay-fail-text" id="pay-fail-text" data-testid="pay-fail-text"></span>
                    <button
                      class="btn-retry"
                      type="button"
                      id="pay-retry-button"
                      data-testid="pay-retry-button"
                    >
                      Try again
                    </button>
                  </div>
                  <div class="pay-status" id="pay-status" data-testid="pay-status" hidden></div>
                  
                  <button
                    class="btn-secondary btn-full"
                    type="button"
                    id="pay-status-retry-button"
                    data-testid="pay-status-retry-button"
                    hidden
                  >
                    Check split status
                  </button>
                  <button
                    class="btn-primary btn-full"
                    type="button"
                    id="pay-check-button"
                    data-testid="pay-check-button"
                    hidden
                  >
                    Check payment
                  </button>
                  <button
                    class="btn-secondary btn-full"
                    type="button"
                    id="pay-reader-cancel-button"
                    data-testid="pay-reader-cancel-button"
                    hidden
                  >
                    Cancel and take payment another way
                  </button>
                  
                  <div
                    class="pay-tip-split"
                    id="pay-tip-split"
                    data-testid="pay-tip-split"
                    hidden
                  ></div>
                </div>
              </div>`;
